import { useEffect, useState } from "react";
import { Facebook, Instagram, Linkedin, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";

const NET_ICON = { instagram: Instagram, facebook: Facebook, linkedin: Linkedin };
const NET_COLOR = { instagram: "text-pink-500", facebook: "text-blue-600", linkedin: "text-sky-700" };

function AccessStatus({ n }) {
  if (!n.enabled) return null;
  const ok = n.complete;
  const txt = ok
    ? (n.mode === "delegated"
      ? "Accès DGA attribué par le client"
      : `Identifiants renseignés${n.source === "agency" ? " (par l'agence)" : ""}`)
    : "En attente du client";
  return (
    <p className={`text-[11px] font-medium flex items-center gap-1.5 ${ok ? "text-green-700" : "text-red-600"}`}
      data-testid={`social-admin-status-${n.network}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${ok ? "bg-green-500" : "bg-red-500"}`} />
      {txt}
    </p>
  );
}

function AdminCreds({ clientId, n, onSaved }) {
  const [login, setLogin] = useState(n.login || "");
  const [pw, setPw] = useState("");
  const [editPw, setEditPw] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setLogin(n.login || ""); setPw(""); setEditPw(false); }, [n.login, n.password_configured, n.delegated]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { login: login.trim() };
      if (!n.password_configured || editPw) payload.password = pw;
      await api.put(`/graphisme/clients/${clientId}/social/${n.network}`, payload);
      toast.success(`${n.label} — accès enregistrés (chiffrés)`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-3 border-t border-neutral-200 pt-3 space-y-2.5">
      <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">
        Accès au compte — saisissable par le client ou par vous (s'il vous les a transmis)
      </p>
      <div className="grid sm:grid-cols-2 gap-3">
        <Input value={login} onChange={(e) => setLogin(e.target.value)}
          placeholder="Identifiant / adresse e-mail" data-testid={`social-admin-login-${n.network}`} />
        {n.password_configured && !editPw ? (
          <div className="flex items-center gap-2">
            <span className="flex-1 border border-neutral-200 rounded-lg px-3 py-2 text-sm tracking-widest text-neutral-500 bg-neutral-50" data-testid={`social-admin-pw-masked-${n.network}`}>••••••••••••</span>
            <Button type="button" variant="outline" size="sm" onClick={() => { setEditPw(true); setPw(""); }} data-testid={`social-admin-pw-edit-${n.network}`}>Modifier</Button>
          </div>
        ) : (
          <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)}
            placeholder="Mot de passe (stocké chiffré)" autoComplete="new-password" data-testid={`social-admin-password-${n.network}`} />
        )}
      </div>
      <div className="flex items-center gap-3">
        <Button size="sm" variant="outline" onClick={save} disabled={busy} data-testid={`social-admin-save-creds-${n.network}`}>
          {busy ? "Enregistrement…" : "Enregistrer les accès"}
        </Button>
        <span className="text-[11px] text-neutral-400">Le mot de passe est chiffré et n'est plus lisible après enregistrement.</span>
      </div>
    </div>
  );
}

export function SocialNetworksAdmin({ clientId }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/graphisme/clients/${clientId}/social`).then((r) => {
    setData(r.data);
    const f = {};
    (r.data.networks || []).forEach((n) => { f[n.network] = { enabled: n.enabled, handle: n.handle, url: n.url }; });
    setForm(f);
  }).catch(() => setData({ networks: [] }));
  useEffect(() => { load(); }, [clientId]);

  if (data === null || form === null) return null;
  const byNet = {};
  (data.networks || []).forEach((n) => { byNet[n.network] = n; });

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/graphisme/clients/${clientId}/social`, { networks: form });
      toast.success("Réseaux sociaux enregistrés — l'espace client est mis à jour");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const set = (net, key, val) => setForm((f) => ({ ...f, [net]: { ...f[net], [key]: val } }));

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-5 mb-6" data-testid="social-admin">
      <h3 className="font-heading font-bold mb-1">Réseaux sociaux gérés</h3>
      <p className="text-xs text-neutral-400 mb-4">Réseaux inclus dans la prestation CM — seuls ces réseaux apparaissent dans l'espace client, qui y renseigne ses accès.</p>
      <div className="space-y-4">
        {["instagram", "facebook", "linkedin"].map((net) => {
          const Icon = NET_ICON[net];
          const n = byNet[net] || { network: net };
          const f = form[net] || { enabled: false, handle: "", url: "" };
          return (
            <div key={net} className={`border rounded-xl p-4 transition-colors ${f.enabled ? "border-neutral-300 bg-neutral-50/50" : "border-neutral-200"}`}
              data-testid={`social-admin-${net}`}>
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 text-sm font-medium cursor-pointer" data-testid={`social-admin-enable-wrap-${net}`}>
                  <input type="checkbox" checked={f.enabled} onChange={(e) => set(net, "enabled", e.target.checked)}
                    className="rounded border-neutral-300" data-testid={`social-admin-enable-${net}`} />
                  <Icon className={`w-4 h-4 ${NET_COLOR[net]}`} /> {n.label || net}
                </label>
                <div className="ml-auto"><AccessStatus n={n} /></div>
              </div>
              {f.enabled && (
                <div className="grid sm:grid-cols-2 gap-3 mt-3">
                  <Input value={f.handle} onChange={(e) => set(net, "handle", e.target.value)}
                    placeholder={net === "instagram" ? "Nom du compte / @identifiant" : net === "facebook" ? "Nom de la Page / @identifiant" : "Nom de la Page"}
                    data-testid={`social-admin-handle-${net}`} />
                  <Input value={f.url} onChange={(e) => set(net, "url", e.target.value)}
                    placeholder={net === "linkedin" ? "URL de la Page LinkedIn (facultatif)" : "URL du compte / de la Page (facultatif)"}
                    data-testid={`social-admin-url-${net}`} />
                </div>
              )}
              {f.enabled && <AdminCreds clientId={clientId} n={n} onSaved={load} />}
            </div>
          );
        })}
      </div>
      <Button size="sm" onClick={save} disabled={busy} className="mt-4 bg-neutral-900 hover:bg-neutral-800 text-white gap-1.5" data-testid="social-admin-save">
        <Save className="w-3.5 h-3.5" /> {busy ? "Enregistrement…" : "Enregistrer"}
      </Button>
    </div>
  );
}
